import React, { useState, useRef, useCallback } from 'react';
import {
  Crop,
  RotateCw,
  ZoomIn,
  ZoomOut,
  Move,
  Check,
  X,
  Upload,
  Loader2
} from 'lucide-react';

interface ImageCropperProps {
  onCrop?: (croppedImageUrl: string) => void;
  onCancel?: () => void;
  aspectRatio?: number; // width/height ratio, e.g., 1 for square
  maxWidth?: number;
  maxHeight?: number;
  className?: string;
}

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

const ImageCropper: React.FC<ImageCropperProps> = ({
  onCrop,
  onCancel,
  aspectRatio = 1, // Default to square
  maxWidth = 400,
  maxHeight = 400,
  className = ''
}) => {
  const [image, setImage] = useState<string | null>(null);
  const [cropArea, setCropArea] = useState<CropArea>({ x: 0, y: 0, width: 200, height: 200 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageUrl = e.target?.result as string;
        setImage(imageUrl);
        // Reset crop area when new image is loaded
        setTimeout(() => {
          initializeCropArea();
        }, 100);
      };
      reader.readAsDataURL(file);
    }
  };

  const initializeCropArea = () => {
    if (!imageRef.current || !containerRef.current) return;

    const container = containerRef.current.getBoundingClientRect();
    const img = imageRef.current.getBoundingClientRect();
    
    // Calculate initial crop area (centered, 50% of image size)
    const cropSize = Math.min(img.width, img.height) * 0.5;
    const cropWidth = aspectRatio >= 1 ? cropSize : cropSize * aspectRatio;
    const cropHeight = aspectRatio >= 1 ? cropSize / aspectRatio : cropSize;
    
    setCropArea({
      x: (img.width - cropWidth) / 2,
      y: (img.height - cropHeight) / 2,
      width: cropWidth,
      height: cropHeight
    });
  };

  const handleMouseDown = (e: React.MouseEvent, action: 'drag' | 'resize') => {
    e.preventDefault();
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    setDragStart({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });

    if (action === 'drag') {
      setIsDragging(true);
    } else {
      setIsResizing(true);
    }
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!containerRef.current || (!isDragging && !isResizing)) return;

    const rect = containerRef.current.getBoundingClientRect();
    const currentX = e.clientX - rect.left;
    const currentY = e.clientY - rect.top;
    const deltaX = currentX - dragStart.x;
    const deltaY = currentY - dragStart.y;

    if (isDragging) {
      setCropArea(prev => ({
        ...prev,
        x: Math.max(0, Math.min(prev.x + deltaX, rect.width - prev.width)),
        y: Math.max(0, Math.min(prev.y + deltaY, rect.height - prev.height))
      }));
    } else if (isResizing) {
      setCropArea(prev => {
        const newWidth = Math.max(50, prev.width + deltaX);
        const newHeight = aspectRatio ? newWidth / aspectRatio : Math.max(50, prev.height + deltaY);
        
        return {
          ...prev,
          width: Math.min(newWidth, rect.width - prev.x),
          height: Math.min(newHeight, rect.height - prev.y)
        };
      });
    }

    setDragStart({ x: currentX, y: currentY });
  }, [isDragging, isResizing, dragStart, aspectRatio]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
  }, []);

  React.useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  const handleZoom = (direction: 'in' | 'out') => {
    const newScale = direction === 'in' 
      ? Math.min(scale * 1.1, 3) 
      : Math.max(scale * 0.9, 0.5);
    setScale(newScale);
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const cropImage = async () => {
    if (!imageRef.current || !canvasRef.current) return;

    setIsProcessing(true);

    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const img = imageRef.current;
      const imgRect = img.getBoundingClientRect();
      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!containerRect) return;

      // Calculate the actual image dimensions vs displayed dimensions
      const scaleX = img.naturalWidth / imgRect.width;
      const scaleY = img.naturalHeight / imgRect.height;

      // Calculate crop area in actual image coordinates
      const actualCropArea = {
        x: cropArea.x * scaleX,
        y: cropArea.y * scaleY,
        width: cropArea.width * scaleX,
        height: cropArea.height * scaleY
      };

      // Set canvas size to desired output size
      canvas.width = maxWidth;
      canvas.height = maxHeight;

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Apply transformations
      ctx.save();
      
      // Move to center for rotation
      ctx.translate(canvas.width / 2, canvas.height / 2);
      
      // Apply rotation
      ctx.rotate((rotation * Math.PI) / 180);
      
      // Apply scale
      ctx.scale(scale, scale);

      // Draw the cropped portion of the image
      ctx.drawImage(
        img,
        actualCropArea.x,
        actualCropArea.y,
        actualCropArea.width,
        actualCropArea.height,
        -canvas.width / 2,
        -canvas.height / 2,
        canvas.width,
        canvas.height
      );

      ctx.restore();

      // Convert canvas to blob and create URL
      canvas.toBlob((blob) => {
        if (blob) {
          const croppedImageUrl = URL.createObjectURL(blob);
          onCrop?.(croppedImageUrl);
        }
        setIsProcessing(false);
      }, 'image/jpeg', 0.9);
    } catch (error) {
      console.error('Error cropping image:', error);
      setIsProcessing(false);
    }
  };

  if (!image) {
    return (
      <div className={`w-full max-w-md mx-auto ${className}`}>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-2">Select an image to crop</p>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Choose Image
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full max-w-2xl mx-auto ${className}`}>
      {/* Controls */}
      <div className="flex items-center justify-between mb-4 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => handleZoom('out')}
            className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded"
            title="Zoom Out"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-sm text-gray-600 min-w-[60px] text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            type="button"
            onClick={() => handleZoom('in')}
            className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded"
            title="Zoom In"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
        </div>

        <button
          type="button"
          onClick={handleRotate}
          className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded"
          title="Rotate 90°"
        >
          <RotateCw className="w-4 h-4" />
        </button>

        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1 text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-50"
          >
            <X className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={cropImage}
            disabled={isProcessing}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
          >
            {isProcessing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            <span>{isProcessing ? 'Processing...' : 'Crop'}</span>
          </button>
        </div>
      </div>

      {/* Crop Area */}
      <div 
        ref={containerRef}
        className="relative bg-gray-100 rounded-lg overflow-hidden"
        style={{ aspectRatio: '16/9', minHeight: '300px' }}
      >
        <img
          ref={imageRef}
          src={image}
          alt="Crop preview"
          className="w-full h-full object-contain"
          style={{
            transform: `scale(${scale}) rotate(${rotation}deg)`,
            transformOrigin: 'center'
          }}
          onLoad={initializeCropArea}
        />

        {/* Crop Overlay */}
        <div
          className="absolute border-2 border-blue-500 bg-blue-500 bg-opacity-10 cursor-move"
          style={{
            left: cropArea.x,
            top: cropArea.y,
            width: cropArea.width,
            height: cropArea.height
          }}
          onMouseDown={(e) => handleMouseDown(e, 'drag')}
        >
          {/* Crop Area Label */}
          <div className="absolute -top-6 left-0 text-xs text-blue-600 bg-white px-2 py-1 rounded shadow">
            <Move className="w-3 h-3 inline mr-1" />
            Drag to move
          </div>

          {/* Resize Handle */}
          <div
            className="absolute bottom-0 right-0 w-4 h-4 bg-blue-500 cursor-se-resize"
            onMouseDown={(e) => handleMouseDown(e, 'resize')}
          >
            <div className="absolute bottom-1 right-1 w-2 h-2 border-r-2 border-b-2 border-white"></div>
          </div>
        </div>

        {/* Dark overlay outside crop area */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Top */}
          <div 
            className="absolute top-0 left-0 right-0 bg-black bg-opacity-50"
            style={{ height: cropArea.y }}
          />
          {/* Bottom */}
          <div 
            className="absolute left-0 right-0 bg-black bg-opacity-50"
            style={{ 
              top: cropArea.y + cropArea.height,
              bottom: 0
            }}
          />
          {/* Left */}
          <div 
            className="absolute left-0 bg-black bg-opacity-50"
            style={{ 
              top: cropArea.y,
              width: cropArea.x,
              height: cropArea.height
            }}
          />
          {/* Right */}
          <div 
            className="absolute right-0 bg-black bg-opacity-50"
            style={{ 
              top: cropArea.y,
              left: cropArea.x + cropArea.width,
              bottom: 0,
              height: cropArea.height
            }}
          />
        </div>
      </div>

      {/* Hidden canvas for processing */}
      <canvas
        ref={canvasRef}
        className="hidden"
        width={maxWidth}
        height={maxHeight}
      />

      {/* Instructions */}
      <div className="mt-4 text-sm text-gray-600 text-center">
        <p>Drag the blue area to position your crop, use the handle to resize, and adjust zoom/rotation as needed.</p>
      </div>
    </div>
  );
};

export default ImageCropper;